import type { GrayWhaleState, World } from '../types';
import { EASTERN_NORTH_PACIFIC_GRAY_WHALE, calvingEve, stepGrayWhales } from '../graywhales';
import { GRAY_WHALE_SUPPLY, GRAY_WHALE_TABLE, GRAY_WHALE_TABLE_YEAR } from '../data/graywhales/enp';
import { STREAM, stockStream } from '../streams';
import { EXPECTED_DRAWS, stockDraws, type PopulationDraws } from '../population';
import { epochDayFromCivil, epochDayOf } from '../calendar';
import { wholeSeals } from './seals';

/** The place the gray whales' stream is named for: their ocean's north, the North Pacific, whose bands they live in, and the Arctic beyond it. */
export const GRAY_WHALE_PLACE = 'north_pacific';

/** The gray whales' draws on their population's stream of the day (`stockDraws`, one population): binomial deaths and strikes on each class's count, Poisson draws of the day's calves. */
export function grayWhaleDraws(world: { seed: number; day: number }, streamOf: typeof stockStream = stockStream): PopulationDraws {
  return stockDraws(world, STREAM.grayWhale, GRAY_WHALE_PLACE, streamOf);
}

/** A day of the eastern North Pacific gray whales (`graywhales.ts`) on their own stream of the day (`migrants.ts` steps them). */
export function stepEasternGrayWhales(world: World, streamOf: typeof stockStream = stockStream): void {
  stepGrayWhales(EASTERN_NORTH_PACIFIC_GRAY_WHALE, world.migrants.grayWhale, epochDayOf(world), GRAY_WHALE_SUPPLY, grayWhaleDraws(world, streamOf));
}

/**
 * The gray whales as a world opens on a day: the table (`data/graywhales/`,
 * the eve of the calving season that opens NOAA's 2025/2026 count) on its own
 * day, or, for a world opening before it, on the last eve on or before the
 * opening day (as if that year's, NOAA's count held for the two seasons
 * before); then
 * stepped at expected values to the opening day, each sex's classes made
 * whole whales (`wholeSeals`; no draw: a world's opening draws nothing), the
 * whales of one and over it looks back on rounded to whole whales.
 */
export function openingGrayWhales(epochDay: number): GrayWhaleState {
  const known = openedGrayWhales.get(epochDay) ?? openGrayWhales(epochDay);
  openedGrayWhales.set(epochDay, known);
  return { females: [...known.females], males: [...known.males], recent: [...known.recent], calvingRate: known.calvingRate };
}

/** The openings worked out, by day: the same for every seed, and a world years past the table's day steps every day to it (review M116: 46 ms a world in 2071). */
const openedGrayWhales = new Map<number, GrayWhaleState>();

function openGrayWhales(epochDay: number): GrayWhaleState {
  const life = EASTERN_NORTH_PACIFIC_GRAY_WHALE;
  const tableEve = epochDayFromCivil(GRAY_WHALE_TABLE_YEAR, life.calving.from[0], life.calving.from[1]) - 1;
  const eve = epochDay > tableEve ? tableEve : calvingEve(life, epochDay);
  const w: GrayWhaleState = { females: [...GRAY_WHALE_TABLE.females], males: [...GRAY_WHALE_TABLE.males], recent: [...GRAY_WHALE_TABLE.recent], calvingRate: GRAY_WHALE_TABLE.calvingRate };
  for (let d = eve + 1; d <= epochDay; d++) stepGrayWhales(life, w, d, GRAY_WHALE_SUPPLY, EXPECTED_DRAWS);
  return { females: wholeSeals(w.females), males: wholeSeals(w.males), recent: w.recent.map(Math.round), calvingRate: w.calvingRate };
}
